import type { PortalNotificationContext } from '@/modules/notifications/portal-notifications.api'
import { useMemo, useRef } from 'react'
import { useClientPortalLayout } from '@/contexts/ClientPortalLayoutContext'
import { useWorkspaceSession } from '@/hooks/useWorkspaceSession'
import { getCurrentClientPortalContext } from '@/lib/api/client-portal-context'

import { isValidUUID } from '@scrambl/shared'
import {
  linkAbortSignals,
  SessionChangedError,
} from '@scrambl/shared/session-scope'

/** The entity guard resolves portal access; a selected staff workspace is optional. */
export function usePortalNotificationSession(
  entityId: string | null | undefined,
) {
  const workspace = useWorkspaceSession()
  const layout = useClientPortalLayout()
  const route = getCurrentClientPortalContext()?.entityId ?? null
  const organizationId =
    layout.currentEntity?.id === entityId
      ? layout.currentEntity.organization.id
      : null
  const preview = layout.isInternalViewer
  const identity = JSON.stringify([
    workspace.key,
    entityId,
    route,
    organizationId,
    preview,
  ])
  const current = useRef(identity)
  current.current = identity
  return useMemo(() => {
    const enabled =
      !!workspace.ownerUserId &&
      isValidUUID(workspace.ownerUserId) &&
      !!entityId &&
      isValidUUID(entityId) &&
      entityId === route
    const assertCurrent = () => {
      workspace.assertUnchanged()
      if (
        !enabled ||
        workspace.signal.aborted ||
        current.current !== identity ||
        getCurrentClientPortalContext()?.entityId !== entityId
      )
        throw new SessionChangedError()
    }
    return {
      enabled,
      preview,
      key: {
        workspaceSession: workspace.key,
        portalEntityId: entityId ?? null,
        organizationId,
        preview,
      },
      assertCurrent,
      async run<T>(
        signal: AbortSignal,
        action: (context: PortalNotificationContext) => Promise<T>,
      ) {
        assertCurrent()
        const linked = linkAbortSignals([signal, workspace.signal])
        try {
          linked.signal.throwIfAborted()
          const value = await action({
            userId: workspace.ownerUserId!,
            entityId: entityId!,
            organizationId,
            signal: linked.signal,
          })
          assertCurrent()
          linked.signal.throwIfAborted()
          return value
        } finally {
          linked.dispose()
        }
      },
    }
  }, [workspace, entityId, route, organizationId, preview, identity])
}
